import React, { useState } from 'react';
import { UserAvatar } from '@/components/UserAvatar';
import VotingButtons from '@/components/VotingButtons';
import EditIdeaDialog from '@/components/EditIdeaDialog';
import { categoryMeta } from '@/lib/categories';
import { IDEA } from '@/constants/testIds';
import { ExternalLink, Sun, Sunset, Moon, Clock, MoreHorizontal, Pencil, Trash2, Loader2 } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const TIME_ICON = {
    morning: Sun,
    afternoon: Sun,
    evening: Sunset,
    anytime: Clock,
    night: Moon,
};

export default function IdeaCard({ idea, onOpen, tripId, onVoteChanged, onChanged, showVoting = true, currentUserId, tripOwnerId }) {
    const cat = categoryMeta(idea.category);
    const Icon = cat.icon;
    const TimeIcon = idea.best_time ? (TIME_ICON[idea.best_time] || Clock) : null;
    const breakdown = idea.vote_breakdown || { must: 0, nice: 0, skip: 0 };

    const isSaver = !!currentUserId && currentUserId === idea.user_id;
    const isOwner = !!currentUserId && !!tripOwnerId && currentUserId === tripOwnerId;
    const canEdit = isSaver;
    const canDelete = isSaver || isOwner;
    const showMenu = canEdit || canDelete;

    const [editOpen, setEditOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async (e) => {
        e?.preventDefault?.();
        setDeleting(true);
        try {
            await api.delete(`/ideas/${idea.inspiration_id}`);
            toast.success('Idea removed');
            setConfirmOpen(false);
            onChanged?.({ type: 'deleted', ideaId: idea.inspiration_id });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not delete');
        } finally {
            setDeleting(false);
        }
    };

    const handleEditSaved = (updated) => {
        onChanged?.({ type: 'updated', idea: updated });
    };

    return (
        <div
            data-testid={IDEA.card}
            className="break-inside-avoid mb-5 w-full ns-card ns-card-hover overflow-hidden group cursor-pointer relative"
            onClick={() => onOpen?.(idea)}
        >
            {showMenu && (
                <div
                    className="absolute top-3 right-3 z-10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                data-testid={IDEA.cardMenuTrigger}
                                aria-label="Idea actions"
                                className="w-8 h-8 rounded-full bg-white/95 backdrop-blur border border-[var(--ns-border)] flex items-center justify-center text-[var(--ns-text2)] hover:text-[var(--ns-text)] hover:border-[var(--ns-clay)] transition shadow-sm"
                            >
                                <MoreHorizontal size={16} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-2xl min-w-[140px]">
                            {canEdit && (
                                <DropdownMenuItem
                                    onSelect={() => setEditOpen(true)}
                                    data-testid={IDEA.cardMenuEdit}
                                    className="cursor-pointer text-sm"
                                >
                                    <Pencil size={14} className="mr-2" /> Edit
                                </DropdownMenuItem>
                            )}
                            {canDelete && (
                                <DropdownMenuItem
                                    onSelect={() => setConfirmOpen(true)}
                                    data-testid={IDEA.cardMenuDelete}
                                    className="cursor-pointer text-sm text-[#D05C45] focus:text-[#D05C45]"
                                >
                                    <Trash2 size={14} className="mr-2" /> Delete
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}

            {idea.thumbnail ? (
                <div className="relative overflow-hidden">
                    <img
                        src={idea.thumbnail}
                        alt={idea.location_name}
                        className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03]"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-white/90 backdrop-blur px-2.5 py-1 rounded-full font-medium"
                        style={{ color: cat.color }}
                    >
                        <Icon size={12} /> {cat.label}
                    </span>
                </div>
            ) : (
                <div
                    data-testid={`${IDEA.card}-placeholder`}
                    className="relative h-40 flex items-center justify-center overflow-hidden"
                    style={{
                        background: `linear-gradient(135deg, ${cat.color}22 0%, ${cat.color}44 100%)`,
                    }}
                >
                    <Icon size={56} style={{ color: cat.color, opacity: 0.75 }} strokeWidth={1.5} />
                    <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-white/90 backdrop-blur px-2.5 py-1 rounded-full font-medium"
                        style={{ color: cat.color }}
                    >
                        <Icon size={12} /> {cat.label}
                    </span>
                </div>
            )}
            <div className="p-5">
                <h3 className="font-display text-lg font-semibold tracking-tight text-[var(--ns-text)] leading-snug">
                    {idea.location_name}
                </h3>
                {idea.neighborhood && (
                    <div className="text-xs text-[var(--ns-text3)] mt-1">{idea.neighborhood}</div>
                )}
                {idea.ai_summary && (
                    <p className="text-sm text-[var(--ns-text2)] mt-2 leading-relaxed line-clamp-3">{idea.ai_summary}</p>
                )}
                {idea.note && (
                    <p className="text-sm text-[var(--ns-text)] mt-3 leading-relaxed line-clamp-2 italic border-l-2 pl-3" style={{ borderColor: 'var(--ns-clay)' }}>
                        &ldquo;{idea.note}&rdquo;
                    </p>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--ns-border)]">
                    <div className="flex items-center gap-2 min-w-0">
                        <UserAvatar name={idea.saved_by?.name} picture={idea.saved_by?.picture} size={24} />
                        <span className="text-xs text-[var(--ns-text2)] truncate">{idea.saved_by?.name || 'You'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[var(--ns-text3)]">
                        {TimeIcon && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest">
                                <TimeIcon size={11} /> {idea.best_time}
                            </span>
                        )}
                        {idea.source_domain && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest">
                                <ExternalLink size={11} /> {idea.source_domain}
                            </span>
                        )}
                    </div>
                </div>

                {showVoting && tripId && (
                    <div className="mt-4">
                        <VotingButtons
                            ideaId={idea.inspiration_id}
                            tripId={tripId}
                            initialVote={idea.my_vote}
                            breakdown={breakdown}
                            score={idea.priority_score || 0}
                            onVoted={() => onVoteChanged?.()}
                            compact={false}
                        />
                    </div>
                )}
            </div>

            {canEdit && (
                <EditIdeaDialog
                    idea={idea}
                    open={editOpen}
                    onOpenChange={setEditOpen}
                    onSaved={handleEditSaved}
                />
            )}

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent onClick={(e) => e.stopPropagation()} className="rounded-3xl border-[var(--ns-border)]">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display tracking-tight">Delete this idea?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[var(--ns-text2)]">
                            This can&apos;t be undone. Votes on this idea will also be removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid={IDEA.cardDeleteCancel}
                            className="rounded-full"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid={IDEA.cardDeleteConfirm}
                            onClick={handleDelete}
                            disabled={deleting}
                            className="rounded-full text-white"
                            style={{ background: '#D05C45' }}
                        >
                            {deleting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Deleting…</> : 'Delete idea'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
